import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Card, PageTitle, fmtDate } from "./shared";
import { Switch } from "../../components/ui/switch";

const inputCls = "h-11 w-full rounded-xl border border-neutral-200 px-3 text-sm outline-none focus:border-[var(--saffron)]";

const CreateForm = ({ onDone }) => {
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/staff/users", f);
      toast.success("Telecaller created");
      setF({ name: "", email: "", password: "" });
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <Card>
      <p className="mb-3 font-semibold">Add telecaller</p>
      <form onSubmit={submit} className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]" data-testid="team-create-form">
        <input className={inputCls} placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="team-name-input" />
        <input className={inputCls} type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="team-email-input" />
        <input className={inputCls} type="password" placeholder="Temporary password (8+)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="team-password-input" />
        <button className="btn-saffron h-11 rounded-full px-5 text-sm font-semibold" data-testid="team-create-button">Create</button>
      </form>
    </Card>
  );
};

export default function Team() {
  const { data: users = [], refetch } = useQuery({ queryKey: ["staff-users"], queryFn: () => api.get("/staff/users").then((r) => r.data) });

  const toggle = async (u) => {
    try {
      await api.patch(`/staff/users/${u.id}`, { active: !u.active });
      refetch();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reset = async (u) => {
    const password = window.prompt(`New password for ${u.name} (min 8 characters)`);
    if (!password) return;
    try {
      await api.post(`/staff/users/${u.id}/reset-password`, { password });
      toast.success("Password reset");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="staff-team-page">
      <PageTitle>Team</PageTitle>
      <CreateForm onDone={refetch} />
      <Card className="p-0">
        <ul className="divide-y divide-neutral-100">
          {users.map((u) => (
            <li key={u.id} className="flex flex-wrap items-center gap-3 px-5 py-4" data-testid={`team-row-${u.email}`}>
              <div className="min-w-[200px] flex-1">
                <p className="font-semibold">{u.name} <span className="ml-1 text-xs font-normal text-neutral-500">{u.role === "super_admin" ? "Super Admin" : "Telecaller"}</span></p>
                <p className="text-xs text-neutral-500">{u.email} · added {fmtDate(u.created_at)}</p>
              </div>
              {u.role !== "super_admin" && (
                <>
                  <label className="flex items-center gap-2 text-sm text-neutral-600">
                    <Switch checked={u.active} onCheckedChange={() => toggle(u)} data-testid={`team-active-toggle-${u.email}`} />
                    {u.active ? "Active" : "Deactivated"}
                  </label>
                  <button onClick={() => reset(u)} className="h-10 rounded-full border border-neutral-300 px-4 text-sm hover:border-neutral-500" data-testid={`team-reset-button-${u.email}`}>
                    Reset password
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
